/**
 * /projects/gantt — Gantt multi-commessa.
 * - Carica tutte le commesse del tenant e, per ognuna, le sue project-tasks
 * - Riga "macro" per commessa (colore per healthStatus) + sotto-riga per ogni task
 * - Sidebar con filtri (fase, priorità, PM, cliente) e toggle "Mostra archivio"
 *
 * Il disegno è il **componente condiviso** `GanttChart`, lo stesso di `/tasks/gantt` e del
 * Gantt WBS nel dettaglio commessa: colonna nomi fissa allo scroll, timeline a giorni,
 * weekend ombreggiati, linea di oggi. Prima questa pagina usava `gantt-task-react`, che
 * portava CSS, font, header e altezze propri e stonava con tutto il resto dell'app.
 *
 * Qui le barre **non si trascinano**: una riga macro è un aggregato (le date vengono dalle
 * task) e il salvataggio in blocco non esiste sul BE. Il click apre la commessa.
 */
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Badge, Card, Group, MultiSelect, Paper, ScrollArea, Select, Stack, Switch, Text, Title,
} from '@mantine/core';
import { QueryErrorAlert } from '@/shared/components/QueryErrorAlert';
import { IconCalendarTime, IconTimeline } from '@tabler/icons-react';
import { EmptyState } from '@/shared/components/EmptyState';
import { InlineLoader } from '@/shared/components/LoadingState';
import { useMemo, useState } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { fetchList } from '@/shared/api/jsonapi/client';
import { HealthBadge, PriorityBadge } from '@/features/projects/HealthBadge';
import { healthColorValue } from '@/features/projects/HealthBadge.utils';

/** La legenda della stampa: la salute del progetto, le attività in tinta chiara. */
const PRINT_LEGEND = [
  { label: 'On track', color: healthColorValue('green') },
  { label: 'A rischio', color: healthColorValue('yellow') },
  { label: 'Critico', color: healthColorValue('red') },
  { label: 'Non valutato', color: healthColorValue(null) },
];
import { PHASE_OPTIONS, phaseLabel } from '@/features/projects/phase';
import { tenantClientOptions, type ClientLite, type TenantClientRow } from '@/features/projects/customerLabel';
import { GanttChart, type GanttChartRow } from '@/features/tasks/desktop/GanttChart';
import { BAR_ISO } from '@/features/tasks/desktop/ganttDrag';
import type { Denormalized } from '@/shared/api/jsonapi/parser';
import { MaePageHead } from '@/shared/desktop/MaeShell';

interface Project extends Denormalized {
  type: 'projects';
  name?: string;
  code?: string;
  /** v1.21: era `status` a vocabolario libero. */
  phase?: string;
  priority?: string;
  healthStatus?: string;
  startDate?: string | null;
  endDate?: string | null;
  /** v1.21: customer è un tenant-client; la label vive su customer.client. */
  customer?: { id: string; type?: string; client?: ClientLite | null } | null;
  /** Da BE v1.8.0: PM via relationship denormalizzata. */
  projectManager?: { id: string; type?: string; firstName?: string; lastName?: string } | null;
}

interface ProjectTaskRow extends Denormalized {
  type: 'project-tasks';
  name?: string;
  startDate?: string | null;
  endDate?: string | null;
  advancement?: number | null;
  status?: string | null;
  /** Da BE v1.8.0: parent solo come relationship denormalizzata. */
  parent?: { id: string } | null;
  taskType?: string;
}

interface User extends Denormalized { type: 'users'; firstName?: string; lastName?: string; email?: string }

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Bassa' },
  { value: 'medium', label: 'Media' },
  { value: 'high', label: 'Alta' },
  { value: 'urgent', label: 'Urgente' },
];

export const Route = createFileRoute('/_app/projects/gantt')({ component: MultiProjectGanttPage });

function MultiProjectGanttPage() {
  const navigate = useNavigate();
  const [showArchive, setShowArchive] = useState(false);
  const [phaseF, setPhaseF] = useState<string[]>([]);
  const [priorityF, setPriorityF] = useState<string[]>([]);
  const [managerF, setManagerF] = useState<string | null>(null);
  const [customerF, setCustomerF] = useState<string | null>(null);

  const projectsQ = useQuery({
    queryKey: ['projects-multi-gantt'],
    queryFn: () => fetchList<Project>('projects', { page: { size: 100 }, include: ['customer.client', 'projectManager'] }),
  });
  const clientsQ = useQuery({
    queryKey: ['tenant-clients-for-multi-gantt'],
    queryFn: () => fetchList<TenantClientRow>('tenant-clients', { page: { size: 100 }, include: ['client'] }),
  });
  const usersQ = useQuery({
    queryKey: ['users-for-multi-gantt'],
    queryFn: () => fetchList<User>('users', { page: { size: 100 } }),
  });

  /* `?? []` produce un array nuovo a ogni render: senza questo `useMemo` la memoizzazione
     qui sotto non memoizza niente, e ricalcola sempre. */
  const allProjects = useMemo(() => projectsQ.data?.data ?? [], [projectsQ.data?.data]);
  const customerOpts = tenantClientOptions(clientsQ.data?.data ?? []);
  const userOpts = (usersQ.data?.data ?? []).map((u) => ({
    value: u.id,
    label: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email || u.id,
  }));

  const filteredProjects = useMemo(() => {
    return allProjects.filter((p) => {
      if (!showArchive && p.phase === 'archived') return false;
      if (phaseF.length && !phaseF.includes(p.phase ?? '')) return false;
      if (priorityF.length && !priorityF.includes(p.priority ?? '')) return false;
      if (managerF && p.projectManager?.id !== managerF) return false;
      if (customerF && p.customer?.id !== customerF) return false;
      return true;
    });
  }, [allProjects, showArchive, phaseF, priorityF, managerF, customerF]);

  const taskQueries = useQueries({
    queries: filteredProjects.map((p) => ({
      queryKey: ['project-tasks-multi-gantt', p.id],
      queryFn: () =>
        fetchList<ProjectTaskRow>('project-tasks', {
          filter: { 'project.id': { eq: p.id } },
          sort: ['sortOrder', 'startDate'],
          page: { size: 100 },
        }),
      staleTime: 30_000,
    })),
  });

  const rows = useMemo<GanttChartRow[]>(() => {
    const today = dayjs();
    const out: GanttChartRow[] = [];
    filteredProjects.forEach((p, idx) => {
      const tasks = taskQueries[idx]?.data?.data ?? [];
      const taskStarts = tasks.map((t) => (t.startDate ? dayjs(t.startDate) : null)).filter(Boolean) as dayjs.Dayjs[];
      const taskEnds = tasks.map((t) => (t.endDate ? dayjs(t.endDate) : null)).filter(Boolean) as dayjs.Dayjs[];
      const projStart = p.startDate
        ? dayjs(p.startDate)
        : taskStarts.length ? taskStarts.reduce((a, b) => (a.isBefore(b) ? a : b)) : today;
      const projEndRaw = p.endDate
        ? dayjs(p.endDate)
        : taskEnds.length ? taskEnds.reduce((a, b) => (a.isAfter(b) ? a : b)) : today.add(14, 'day');
      const projEnd = projEndRaw.isAfter(projStart) ? projEndRaw : projStart.add(1, 'day');
      const avgProgress = tasks.length
        ? Math.round(tasks.reduce((s, t) => s + (t.advancement ?? 0), 0) / tasks.length)
        : 0;
      const macroColor = healthColorValue(p.healthStatus);

      out.push({
        id: `proj-${p.id}`,
        label: `${p.code ? `[${p.code}] ` : ''}${p.name ?? p.id}`,
        sublabel: `${phaseLabel(p.phase)} · ${tasks.length} attività`,
        color: macroColor,
        progress: avgProgress,
        group: true,
        draggable: false,
        hint: 'Riga di progetto: le date sono aggregate dalle attività.',
        startIso: projStart.format(BAR_ISO),
        endIso: projEnd.format(BAR_ISO),
      });

      for (const t of tasks) {
        const s = t.startDate ? dayjs(t.startDate) : projStart;
        const eRaw = t.endDate ? dayjs(t.endDate) : s.add(2, 'day');
        const e = eRaw.isAfter(s) ? eRaw : s.add(1, 'day');
        out.push({
          id: t.id,
          label: t.name ?? '(senza nome)',
          color: towardsSurface(macroColor, 0.22),
          progress: t.advancement ?? 0,
          indent: true,
          draggable: false,
          startIso: s.format(BAR_ISO),
          endIso: e.format(BAR_ISO),
        });
      }
    });
    return out;
  }, [filteredProjects, taskQueries]);

  const isLoading = projectsQ.isLoading || taskQueries.some((q) => q.isLoading);
  /* Il gantt multi-commessa somma N letture: se una fallisce mostra un piano parziale,
     che per chi pianifica è peggio di nessun piano. */
  const failed = projectsQ.isError ? projectsQ : taskQueries.find((q) => q.isError);

  /** Click su una barra: le righe macro portano al dettaglio commessa. */
  const onSelect = (id: string) => {
    if (id.startsWith('proj-')) void navigate({ to: '/projects/$projectId', params: { projectId: id.slice(5) } });
  };

  return (
    <Stack>
      <MaePageHead
        title="Gantt overview"
        icon={<IconTimeline size={23} />}
        actions={<Switch label="Mostra archivio" checked={showArchive} onChange={(e) => setShowArchive(e.currentTarget.checked)} />}
      />

      <Group align="flex-start" wrap="wrap" gap="md">
        <Paper withBorder p="md" radius="md" style={{ flex: '0 1 300px', minWidth: 260 }}>
          <Stack>
            <Title order={5}>Filtri</Title>
            <MultiSelect label="Fase" data={[...PHASE_OPTIONS]} value={phaseF} onChange={setPhaseF} clearable />
            <MultiSelect label="Priorità" data={PRIORITY_OPTIONS} value={priorityF} onChange={setPriorityF} clearable />
            <Select label="Project Manager" data={userOpts} value={managerF} onChange={setManagerF} clearable searchable />
            <Select label="Cliente" data={customerOpts} value={customerF} onChange={setCustomerF} clearable searchable />
            <Stack gap={4} mt="md">
              <Title order={6}>Progetti ({filteredProjects.length})</Title>
              <ScrollArea h={300} type="auto">
                <Stack gap={4}>
                  {filteredProjects.map((p) => (
                    <Card
                      key={p.id}
                      padding="xs"
                      radius="sm"
                      withBorder
                      style={{ cursor: 'pointer' }}
                      onClick={() => void navigate({ to: '/projects/$projectId', params: { projectId: p.id } })}
                    >
                      <Group justify="space-between" gap={4}>
                        <Text size="xs" fw={600} lineClamp={1}>{p.code ? `[${p.code}] ` : ''}{p.name}</Text>
                        <HealthBadge value={p.healthStatus} size="xs" withLabel={false} />
                      </Group>
                      <Group gap={4} mt={2}>
                        <Badge size="xs" variant="light">{phaseLabel(p.phase)}</Badge>
                        {p.priority && <PriorityBadge value={p.priority} size="xs" />}
                      </Group>
                    </Card>
                  ))}
                </Stack>
              </ScrollArea>
            </Stack>
          </Stack>
        </Paper>

        <div style={{ flex: '3 1 520px', minWidth: 0 }}>
          {failed ? (
            <QueryErrorAlert error={failed.error} title="Impossibile comporre il gantt" onRetry={() => { void projectsQ.refetch(); taskQueries.forEach((q) => void q.refetch()); }} />
          ) : isLoading ? (
            <InlineLoader label="Carico i progetti e le attività…" minHeight={360} />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={IconCalendarTime}
              title="Nessun progetto da visualizzare"
              description="Modifica i filtri o aggiungi un nuovo progetto per popolare il Gantt."
              actionLabel="Vai ai progetti"
              onAction={() => void navigate({ to: '/projects' })}
            />
          ) : (
            <GanttChart
              rows={rows}
              headerLabel="Progetti e attività"
              printTitle="Progetti — Gantt"
              printLegend={PRINT_LEGEND}
              onSelect={onSelect}
              meta={<Text size="sm" c="dimmed">{filteredProjects.length} progetti · {rows.length - filteredProjects.length} attività</Text>}
            />
          )}
        </div>
      </Group>
    </Stack>
  );
}

/** Shade hex color (positive = lighter, negative = darker). */
/**
 * Schiarisce un colore verso **la superficie del tema**, non verso il bianco.
 *
 * ⚠️ Era aritmetica a mano su un esadecimale: `+ Math.round(255 * amt)` su
 * ciascun canale. Due difetti in una riga. Il primo e' che pretendeva un
 * esadecimale, quindi obbligava chi la chiamava a tenere dei letterali — e i
 * quattro colori della salute erano infatti quattro letterali di Tailwind. Il
 * secondo e' che in tema scuro schiarire verso il bianco e' il verso sbagliato:
 * una barra figlia diventava **piu' chiara del proprio fondo scuro** invece di
 * avvicinarsi ad esso.
 *
 * `color-mix` fa le due cose insieme: accetta un `var()` e mescola verso
 * `--surface`, che e' bianco in chiaro e scuro in scuro. Il verso lo decide il
 * tema, non questa funzione.
 */
function towardsSurface(color: string, quota: number): string {
  const hold = Math.round(Math.max(0, Math.min(1, 1 - quota)) * 100);
  return `color-mix(in srgb, ${color} ${hold}%, var(--surface))`;
}
